import { Fragment, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { buildPairingDeepLink } from 'common/helpers'
interface PairingProps {
  paircode: string
  allowCopy?: boolean
}
const PAIRING_DISPLAY_URL = 'signagespace.com'

export const Pairing = ({ paircode, allowCopy = false }: PairingProps) => {
  const [copied, setCopied] = useState(false)
  const qrUrl = buildPairingDeepLink(paircode)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paircode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white font-sans overflow-hidden flex flex-col">
      {/* Ambient orbs */}
      <div className="absolute inset-0 overflow-hidden opacity-40 pointer-events-none">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-gradient-radial from-sky-400/80 to-sky-400/0"
            style={{
              width: `${80 + Math.random() * 120}px`,
              height: `${80 + Math.random() * 120}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 10}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>
      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)' }}
      />

      {/* TOP CHROME — brand */}
      <header className="relative z-10 flex items-center px-6 sm:px-10 md:px-14 lg:px-20 py-5 sm:py-6 md:py-7 short:py-3">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 shrink-0 drop-shadow-[0_0_18px_rgba(56,189,248,0.35)]">
            <img src="/new_logo.png" alt="SignageSpace" />
          </div>
          <span className="text-white/90 text-sm sm:text-base md:text-lg xl:text-xl 2xl:text-2xl font-semibold tracking-tight">
            SignageSpace
          </span>
        </div>
      </header>

      {/* HERO — fills remaining space, content vertically centered */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 sm:px-10 md:px-14 lg:px-20 gap-8 sm:gap-10 md:gap-12 lg:gap-14 short:gap-6">
        {/* Headline + subtitle */}
        <div className="text-center max-w-4xl">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-semibold text-white leading-[1.1] tracking-tight">
            Pair your screen
          </h1>
        </div>

        {/* Steps */}
        <ol className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 lg:gap-5 xl:gap-6">
          {(['Sign in at signagespace.com', 'Go to Screens', 'Add a new screen'] as const).map((label, i, arr) => (
            <Fragment key={label}>
              <li className="flex items-center gap-2.5 sm:gap-3">
                <span className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 rounded-full bg-sky-400/15 border border-sky-400/30 flex items-center justify-center text-sky-400 text-xs sm:text-sm xl:text-base font-bold shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm sm:text-base lg:text-lg xl:text-xl whitespace-nowrap font-medium text-white/75">
                  {label}
                </span>
              </li>
              {i < arr.length - 1 && (
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 xl:w-6 xl:h-6 text-white/25 rotate-90 md:rotate-0 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </Fragment>
          ))}
        </ol>

        {/* Top accent line */}
        <div className="w-full max-w-md xl:max-w-lg 2xl:max-w-xl h-px bg-gradient-to-r from-transparent via-sky-400/25 to-transparent" />

        {/* Code · OR · QR */}
        <div className="grid grid-cols-1 md:grid-cols-[auto_auto_auto] gap-8 md:gap-10 lg:gap-14 xl:gap-16 2xl:gap-20 items-center">
          {/* Pairing code */}
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div className="flex gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3 xl:gap-4">
              {paircode.split('').map((char, i) => (
                <div
                  key={i}
                  className="font-mono font-bold text-sky-400 bg-white/[0.06] border border-white/10 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(56,189,248,0.45),inset_0_1px_1px_rgba(255,255,255,0.12),0_15px_25px_-10px_rgba(0,0,0,0.5)] px-2 sm:px-2.5 md:px-3 lg:px-4 xl:px-5 py-1 sm:py-1.5 md:py-2 lg:py-2.5 xl:py-3 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl min-w-8 sm:min-w-10 md:min-w-14 lg:min-w-16 xl:min-w-20 2xl:min-w-24"
                  style={{
                    animation: 'fadeInUp 0.5s ease-out forwards',
                    animationDelay: `${i * 0.1}s`,
                    transform: 'translateY(20px)',
                    opacity: 0,
                  }}
                >
                  {char}
                </div>
              ))}
            </div>
            <div className="text-[10px] sm:text-xs md:text-sm xl:text-base uppercase tracking-[0.28em] text-white/40 font-semibold mt-1">
              Pairing code
            </div>
            {allowCopy && (
              <button
                onClick={handleCopy}
                className="mt-3 inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-sky-400/10 hover:bg-sky-400/20 border border-sky-400/30 hover:border-sky-400/60 text-sky-300 hover:text-sky-200 text-xs sm:text-sm font-semibold transition-all duration-200"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy code
                  </>
                )}
              </button>
            )}
          </div>

          {/* OR divider — vertical on md+ */}
          <div className="hidden md:flex flex-col items-center gap-3 self-stretch">
            <div className="flex-1 w-px bg-gradient-to-b from-transparent via-sky-400/30 to-transparent" />
            <span className="text-[10px] xl:text-xs uppercase tracking-[0.3em] text-sky-400/60 font-bold">or</span>
            <div className="flex-1 w-px bg-gradient-to-b from-transparent via-sky-400/30 to-transparent" />
          </div>
          {/* OR divider — horizontal on mobile */}
          <div className="flex md:hidden items-center gap-3 w-full max-w-xs">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-sky-400/60 font-bold">or</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
          </div>

          {/* QR */}
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div className="bg-white p-2.5 sm:p-3 md:p-3.5 lg:p-4 rounded-xl lg:rounded-2xl shadow-[0_15px_30px_-10px_rgba(0,0,0,0.5),0_0_40px_rgba(56,189,248,0.3)]">
              <QRCodeSVG
                value={qrUrl}
                size={256}
                level="M"
                marginSize={0}
                bgColor="#ffffff"
                fgColor="#0f172a"
                className="block w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 xl:w-48 xl:h-48 2xl:w-56 2xl:h-56"
              />
            </div>
            <div className="text-[10px] sm:text-xs md:text-sm xl:text-base uppercase tracking-[0.28em] text-white/40 font-semibold mt-1">
              Or scan with phone
            </div>
          </div>
        </div>

        {/* Bottom accent line — mirrors top */}
        <div className="w-full max-w-md xl:max-w-lg 2xl:max-w-xl h-px bg-gradient-to-r from-transparent via-sky-400/25 to-transparent" />
      </main>

      {/* BOTTOM CHROME — help */}
      <footer className="relative z-10 flex items-center justify-center px-6 sm:px-10 md:px-14 lg:px-20 py-5 sm:py-6 md:py-7 short:py-3">
        <span className="text-white/35 text-xs sm:text-sm md:text-base">
          Need help? <span className="text-white/55">signagespace.com/academy</span>
        </span>
      </footer>
    </div>
  )
}
